import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getCertificates, getComplianceData, GetCertificatesOutputType, GetComplianceDataOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Trophy, FileDown, FileText, ExternalLink, Award, ShieldCheck, CheckCircle2, BookOpen, Info } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import {
  buildComplianceCertificateHtml,
  buildModuleCertificateHtml,
  saveCertificateAsPdf,
} from '@/lib/complianceCertificate';

type Data = GetCertificatesOutputType;
type CompSub = GetComplianceDataOutputType['submissions'][0];
type ModuleCert = Data['moduleCerts'][0];
type CategoryCert = Data['categoryCerts'][0];

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d), 'dd MMM yyyy') : ''; } catch { return d || ''; } };

function ComplianceCertCard({ sub, user }: { sub: CompSub; user: any }) {
  const handleSave = () => {
    const staffName = sub.typedName || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
    const html = buildComplianceCertificateHtml({
      staffName,
      formName: sub.formName,
      formType: sub.formType,
      quizScore: sub.quizScore,
      passMark: sub.passMark,
      signedAt: sub.signedAt || new Date().toISOString(),
      role: user?.position || user?.role,
      department: user?.department,
    });
    saveCertificateAsPdf(html);
  };

  return (
    <div className="border border-primary/30 bg-gradient-to-br from-primary/5 to-card rounded-xl p-5 flex items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
          <Award className="w-6 h-6 text-primary" />
        </div>
        <div>
          <p className="font-bold text-foreground text-sm">{sub.formName}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Completed {fmtDate(sub.signedAt)}
            {sub.quizScore != null && <span className="ml-1.5 text-green-600 font-semibold">· {sub.quizScore}% score</span>}
          </p>
          <div className="flex items-center gap-1.5 mt-1">
            <CheckCircle2 className="w-3 h-3 text-primary" />
            <span className="text-xs text-primary font-medium">Certificate of Competence · Rattlebone Sherston Ltd</span>
          </div>
        </div>
      </div>
      <Button size="sm" className="gap-1.5 shrink-0" onClick={handleSave}>
        <FileDown className="w-3.5 h-3.5" /> Save as PDF
      </Button>
    </div>
  );
}

function ModuleCertCard({ cert, user }: { cert: ModuleCert; user: any }) {
  const handleSave = () => {
    const staffName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.email || 'Team Member';
    const html = buildModuleCertificateHtml({
      staffName,
      moduleTitle: cert.moduleTitle,
      categoryName: cert.categoryName,
      quizScore: 100,
      passMark: 80,
      completedDate: cert.issuedDate || new Date().toISOString(),
      role: user?.position || user?.role,
      department: user?.department,
    });
    saveCertificateAsPdf(html);
  };

  return (
    <div className="border border-border bg-card p-4 rounded-xl">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="font-semibold text-foreground text-sm">{cert.moduleTitle}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{cert.categoryName}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Signed off {fmtDate(cert.issuedDate)} · {cert.issuedBy || 'Manager'}</p>
        </div>
        <div className="w-8 h-8 bg-primary/10 flex items-center justify-center rounded-lg shrink-0">
          <Trophy className="w-4 h-4 text-primary" />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Button size="sm" variant="default" className="gap-1.5 w-full" onClick={handleSave}>
          <FileDown className="w-3.5 h-3.5" /> Save as PDF
        </Button>
        {cert.sopCards.length > 0 && (
          <div className="border-t border-border pt-2 mt-1 space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">SOP Procedure Card</p>
            {cert.sopCards.map((card, i) => (
              <a key={i} href={card.url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 text-xs text-primary hover:underline">
                <FileText className="w-3.5 h-3.5" />
                Download SOP Card {cert.sopCards.length > 1 ? i + 1 : ''}
                <ExternalLink className="w-3 h-3 opacity-60" />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryCertCard({ cert, user }: { cert: CategoryCert; user: any }) {
  const handleSave = () => {
    const staffName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.email || 'Team Member';
    const html = buildModuleCertificateHtml({
      staffName,
      moduleTitle: `${cert.categoryName} — Full Competency`,
      categoryName: cert.categoryName,
      quizScore: 100,
      passMark: 80,
      completedDate: cert.issuedDate || new Date().toISOString(),
      role: user?.position || user?.role,
      department: user?.department,
    });
    saveCertificateAsPdf(html);
  };

  return (
    <div className="border-2 border-primary bg-primary/5 p-5 flex items-center justify-between rounded-xl gap-4">
      <div className="flex items-center gap-4">
        <Trophy className="w-8 h-8 text-primary shrink-0" />
        <div>
          <p className="font-bold text-foreground">{cert.categoryName} — Competency Certificate</p>
          <p className="text-sm text-muted-foreground mt-0.5">Issued {fmtDate(cert.issuedDate)} · Signed off by {cert.issuedBy || 'Your manager'}</p>
        </div>
      </div>
      <Button size="sm" onClick={handleSave} className="gap-1.5 shrink-0">
        <FileDown className="w-3.5 h-3.5" /> Save as PDF
      </Button>
    </div>
  );
}

export default function Certificates() {
  const { user } = useAuth();
  const [data, setData] = useState<Data | null>(null);
  const [compliance, setCompliance] = useState<CompSub[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      getCertificates({}),
      getComplianceData({}),
    ]).then(([certs, comp]) => {
      setData(certs);
      setCompliance(comp.submissions.filter(s => s.status === 'Signed'));
    }).finally(() => setLoading(false));
  }, [user]);

  if (loading) return (
    <div className="p-8 space-y-4">
      {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}
    </div>
  );

  const hasCerts = (data?.moduleCerts.length ?? 0) + (data?.categoryCerts.length ?? 0) > 0;
  const hasCompliance = compliance.length > 0;

  return (
    <div className="px-6 md:px-8 py-8 max-w-4xl space-y-10">
      <div>
        <h1 className="text-2xl font-bold text-foreground mb-1">Certificates & Awards</h1>
        <p className="text-muted-foreground text-sm mb-3">Your compliance certificates and training awards. Click <strong>Save as PDF</strong> to open a print preview — your browser will save it as a perfect PDF copy.</p>
        <div className="flex items-start gap-2.5 bg-muted/40 border border-border rounded-lg px-4 py-3">
          <Info className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            Your certificate will open in a new tab with the print dialog. Choose <strong className="text-foreground">Save as PDF</strong> as the destination, set paper size to <strong className="text-foreground">A4 Landscape</strong>, and margins to <strong className="text-foreground">None</strong> for the best result.
          </p>
        </div>
      </div>

      {/* Compliance Certificates */}
      {hasCompliance && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="w-4 h-4 text-primary" />
            <p className="text-sm font-bold text-foreground uppercase tracking-wider">Compliance Certificates</p>
          </div>
          <div className="space-y-3">
            {compliance.map(sub => (
              <ComplianceCertCard key={sub.submissionId} sub={sub} user={user} />
            ))}
          </div>
          <div className="mt-3 p-3 bg-muted/30 border border-border rounded-lg">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-semibold text-foreground">About these certificates: </span>
              These in-house Certificates of Competence confirm that each team member has studied the relevant training materials, passed a written assessment, and made a formal declaration. All training content and assessment questions are based on official UK government guidance and legislation. Issued by Rattlebone Sherston Ltd for internal record-keeping purposes.
            </p>
          </div>
        </div>
      )}

      {/* Category Competency Certificates */}
      {data?.categoryCerts && data.categoryCerts.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-4 h-4 text-primary" />
            <p className="text-sm font-bold text-foreground uppercase tracking-wider">Category Competency Certificates</p>
          </div>
          <div className="space-y-3">
            {data.categoryCerts.map(cert => (
              <CategoryCertCard key={cert.categoryId} cert={cert} user={user} />
            ))}
          </div>
        </div>
      )}

      {/* Module Certificates */}
      {data?.moduleCerts && data.moduleCerts.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-4 h-4 text-primary" />
            <p className="text-sm font-bold text-foreground uppercase tracking-wider">Module Certificates</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {data.moduleCerts.map(cert => (
              <ModuleCertCard key={cert.id} cert={cert} user={user} />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!hasCerts && !hasCompliance && (
        <div className="border border-dashed border-border p-16 text-center text-muted-foreground rounded-xl">
          <Trophy className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p className="font-medium mb-1">No certificates yet</p>
          <p className="text-sm">Complete compliance training or get signed off on modules to earn your certificates.</p>
        </div>
      )}
    </div>
  );
}
